import React, { useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { notifyPreviewRefresh } from "@/components/admin/GlobalPreviewPane";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// [flag, label, description]
const FIELDS = [
  ["show_name", "Name", "Full name of the person filling out the form"],
  ["show_email", "Email address", "Required for us to respond"],
  ["show_phone", "Phone", "Direct phone number (optional)"],
  ["show_company", "Company", "Company or organization name"],
  ["show_address", "Property address / project location", "Where the damage or project is"],
  ["show_license_number", "License number", "Contractor / restoration license #"],
  ["show_industry", "Industry", "Homeowner / restoration co / insurance / etc."],
  ["show_inquiry_type", "Type of inquiry", "Water damage · mold · equipment · authorization · etc."],
  ["show_service", "Service needed", "Mold remediation · water damage · flood · equipment"],
  ["show_damage_timing", "When did the damage happen?", "Within 24 hrs · 24–48 · 48–72 · 72+ · not sure"],
  ["show_power_available", "Is power available?", "Yes · no · limited · not sure · N/A"],
  ["show_message", "Message / free-text", "Big textarea for context"],
];

const ContactFormSettings = () => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/contact-form-settings`, { withCredentials: true });
        setState(data);
      } catch {
        toast.error("Couldn't load contact form settings");
      }
    })();
  }, []);

  const toggle = async (flag, value) => {
    setState((s) => ({ ...s, [flag]: value }));
    setSaving((sv) => ({ ...sv, [flag]: true }));
    try {
      await axios.put(
        `${API}/contact-form-settings`,
        { [flag]: value },
        { withCredentials: true },
      );
      toast.success(`${flag.replace("show_", "").replace(/_/g, " ")} — ${value ? "shown" : "hidden"}`);
      notifyPreviewRefresh();
    } catch {
      toast.error("Save failed");
      setState((s) => ({ ...s, [flag]: !value }));
    } finally {
      setSaving((sv) => ({ ...sv, [flag]: false }));
    }
  };

  if (!state) return <div className="p-6 text-slate-400">Loading…</div>;

  const shownCount = FIELDS.filter(([f]) => state[f]).length;

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="contact-form-settings">
      <div>
        <h1 className="font-display font-black text-2xl text-white">Contact Form Fields</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          Toggle each field on or off. Hidden fields disappear immediately from the Contact page
          form and the homepage form. Great for switching between residential and pro sales
          layouts without editing code.
        </p>
        <div className="mt-3 text-xs text-slate-500">
          {shownCount}/{FIELDS.length} fields currently visible
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden divide-y divide-white/5">
        {FIELDS.map(([flag, label, desc]) => (
          <div key={flag} className="flex items-center gap-4 p-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white">{label}</div>
              <div className="text-xs text-slate-400 mt-0.5">{desc}</div>
            </div>
            {saving[flag] && <Loader2 className="h-4 w-4 animate-spin text-atc-blue" />}
            <Switch
              checked={!!state[flag]}
              onCheckedChange={(v) => toggle(flag, v)}
              data-testid={`toggle-${flag}`}
            />
          </div>
        ))}
      </div>

      <div className="text-xs text-slate-500 pt-4">
        <strong>Tip:</strong> For a pro-only sales layout, hide <em>Damage timing</em>,
        <em> Power available</em>, and <em>Property address</em>, then show{" "}
        <em>License number</em>, <em>Industry</em>, and <em>Company</em>.
      </div>
    </div>
  );
};

export default ContactFormSettings;
